import { z } from 'zod';
import { freeLabelSchema } from '../free-label.js';
import { inquiryStatusSchema } from '../inquiry/status.js';
import { uuidV7Schema } from '../uuid.js';
import { MAX_REJECTION_REASON_LENGTH } from '../validation/decision.js';
/**
 * Ce que l'agent écrit en ouvrant un **contrôle libre** (#419, [M41](../../../../docs/MODELE-DONNEES.md#m41)) :
 * un contrôle fait sur place sans demande. **Rien n'est obligatoire** (M21) — l'agent est seul
 * devant la porte, et le bureau complétera au rattachement.
 *
 * `looseObject` pour la raison de M16 : une tablette plus récente que l'API ne doit pas voir sa
 * saisie refusée.
 */
export const freeInspectionInputSchema = z.looseObject({
    /** L'adresse telle que l'agent la lit sur place, d'un seul tenant — pas de commune à choisir. */
    address: freeLabelSchema,
    clientName: freeLabelSchema,
    occupantName: freeLabelSchema,
    /** Le type de contrôle s'il le connaît, champ libre comme `Inquiry.purpose` (M2). */
    purpose: freeLabelSchema,
});
/**
 * Combien de contrôles libres une équipe peut avoir **en attente de rattachement** — un plafond,
 * pas une règle métier : un logement oublié par tournée, quelques-uns par semaine. Compté par
 * équipe et non par identifiant, que la tablette tire elle-même : sinon un client qui boucle en
 * tirerait de nouveaux sans fin.
 */
export const MAX_PENDING_FREE_INSPECTIONS = 50;
/**
 * Ce que `Proposal.free_inspection` garde : la saisie de l'agent **et l'identifiant de demande que
 * la tablette a tiré** (M7). C'est sous cet identifiant que vivent le brouillon, les pièces et la
 * file d'envoi de la tablette : le perdre, c'est perdre le chemin du renvoi au terrain.
 *
 * ⚠️ **Gardé après le rattachement**, et c'est ce qui permet à la tournée de dire à la tablette
 * quel brouillon reprendre quand la demande rattachée lui revient (`freeInspectionId` de la
 * tournée), et à un rejeu tardif du dépôt d'être reconnu pour ce qu'il est.
 */
export const freeInspectionSchema = freeInspectionInputSchema.extend({ inquiryId: uuidV7Schema });
/**
 * Une ligne de la file « Contrôles libres » du bureau : ce qu'il faut pour rattacher sans ouvrir
 * la saisie entière — la saisie de l'agent, la position relevée, les pièces.
 *
 * ⚠️ **Pas la charge utile entière** : un relevé pèse jusqu'à 700 Kio avec son schéma, et la file
 * en porte plusieurs. Le rattachement fait, la fiche se lit dans « À valider » comme les autres.
 */
export const freeInspectionRowSchema = z.object({
    proposalId: uuidV7Schema,
    submittedAt: z.iso.datetime({ offset: true }),
    team: z.object({ id: uuidV7Schema, name: z.string() }),
    freeInspection: freeInspectionSchema,
    location: z.object({ lat: z.number(), lon: z.number() }).nullable(),
    /**
     * Les pièces jointes, sans la signature ni l'image du schéma. `type` en chaîne et non l'énumération
     * de la saisie : ce module est importé par l'enveloppe de dépôt, qui ne peut pas l'importer en retour.
     */
    media: z.array(z.object({ id: uuidV7Schema, type: z.string(), label: z.string().nullable() })),
});
export const freeInspectionListSchema = z.object({ items: z.array(freeInspectionRowSchema) });
/** Rattacher à une demande **existante** — créée juste avant au besoin, par « Nouvelle demande ». */
export const attachFreeInspectionSchema = z.strictObject({ inquiryId: uuidV7Schema });
export const attachedFreeInspectionSchema = z.object({
    inquiryId: uuidV7Schema,
    status: inquiryStatusSchema,
});
/** Écarter un contrôle libre fait par erreur : un motif, et la saisie reste en base. */
export const dismissFreeInspectionSchema = z.strictObject({
    reason: z.string().trim().min(1).max(MAX_REJECTION_REASON_LENGTH),
});
export function freeInspectionAttachmentOf(status) {
    switch (status) {
        case 'received':
        case 'assigned':
        case 'in_progress':
            return 'submit';
        case 'submitted':
            return 'second_proposal';
        case 'validated':
        case 'archived':
            return 'follow_up';
        case 'dropped':
            return 'new_inquiry';
    }
}
/** Les refus du rattachement — chacun dit à l'écran quoi proposer à la place. */
export const freeInspectionConflictCodeSchema = z.enum([
    'free_inspection_closed',
    'inquiry_closed',
    'inquiry_dropped',
    'too_many_proposals',
]);
export const freeInspectionConflictSchema = z.object({
    code: freeInspectionConflictCodeSchema,
    message: z.string(),
});
//# sourceMappingURL=free-inspection.js.map